import { format, parse } from 'date-fns';
import React from 'react';
import * as Yup from 'yup';
import { Button } from '@economic/taco';

import { t, Trans } from '@lingui/macro';
import { Field, Form } from '../../shared/components/Form';
import date from '../../shared/util/utilities/date';
import { SubmitErrors } from './SubmitErrors';
import { OnboardingCreateAccountingYear, OnboardingStatusAccountingYears } from './types';
import internalApiClient from '../../shared/util/api/internalApiClient';

const onboardingCreateAccountingYear = (data: OnboardingCreateAccountingYear) =>
    internalApiClient.post(`/onboarding/accounting-year`, data);

const validationSchema = () =>
    Yup.object().shape({
        accountingYearType: Yup.number().required(),
    });

function formatDate(d: string) {
    return date.format(date.parseFromISOString(d));
}
function formatISODate(d: string) {
    return format(parse(d), 'YYYY-MM-DD');
}

interface Props {
    data: OnboardingStatusAccountingYears;
    onBack: () => void;
    onDone: () => void;
    children: string | JSX.Element;
}

export default function StepAccountingYear({ data, onBack, onDone, children }: Props) {
    async function saveAccountingYear(values: { accountingYearType: number; from: string; to: string }) {
        const from = formatISODate(
            values.accountingYearType === 0
                ? data.currentAccountingYear[0]
                : values.accountingYearType === 1
                ? data.shiftedAccountingYear[0]
                : values.from
        );
        const to = formatISODate(
            values.accountingYearType === 0
                ? data.currentAccountingYear[1]
                : values.accountingYearType === 1
                ? data.shiftedAccountingYear[1]
                : values.to
        );

        try {
            await onboardingCreateAccountingYear({ from, to });
            onDone();
        } catch (error) {
            if (error?.response?.data?.message) {
                return Promise.reject({
                    validation: {
                        '': error.response.data.message,
                    },
                });
            }

            throw error;
        }
    }

    return (
        <div className="onboarding__container__fixed-size">
            <div className="onboarding onboarding__accounting-year text-center">
                {children}

                <h1>
                    <Trans>Choose an accounting year</Trans>
                </h1>

                <p>
                    <Trans>
                        Create a fiscal year so you know what period your postings belong to. You can change the fiscal year later
                        if needed. (It is most common to use the current calendar year)
                    </Trans>
                </p>

                {data.hasAccountingYears ? (
                    <>
                        <strong>
                            <Trans>Accounting year created.</Trans>
                        </strong>

                        <div className="selected-accounting-year">
                            <span className="from">{formatDate(data.selectedAccountingYear.from)}</span>
                            <span className="to">{formatDate(data.selectedAccountingYear.to)}</span>
                        </div>

                        <p>
                            <span>
                                <Trans>
                                    If you want to change it later, you can do it from <br />
                                    All settings
                                    <i className="long-arrow-right" />
                                    Finance
                                    <i className="long-arrow-right" />
                                    Accounting year.
                                </Trans>
                            </span>
                        </p>

                        <Form onSubmit={onDone} skipNotifications>
                            <div className="onboarding__navigation-buttons">
                                <Button appearance="primary" type="submit">
                                    <Trans>Next</Trans>
                                </Button>
                                <Button
                                    appearance="discrete"
                                    onClick={e => {
                                        e.preventDefault();
                                        onBack();
                                    }}>
                                    <Trans>Back</Trans>
                                </Button>
                            </div>
                        </Form>
                    </>
                ) : (
                    <Form
                        skipNotifications
                        onSubmit={saveAccountingYear}
                        validationSchema={validationSchema}
                        validateOnBlur={false}
                        initialData={{
                            from: data.currentAccountingYear[0],
                            to: data.currentAccountingYear[1],
                        }}>
                        {({ submitErrors }) => (
                            <>
                                <div className="onboarding__form-content">
                                    <Field.NumberRadioGroup
                                        name="accountingYearType"
                                        options={[
                                            {
                                                text: `${t`Use the current calendar year`} (${formatDate(
                                                    data.currentAccountingYear[0]
                                                )} - ${formatDate(data.currentAccountingYear[1])})`,
                                                value: 0,
                                            },
                                            {
                                                text: `${t`Shift by six months`} (${formatDate(
                                                    data.shiftedAccountingYear[0]
                                                )} - ${formatDate(data.shiftedAccountingYear[1])})`,
                                                value: 1,
                                            },
                                            {
                                                text: (
                                                    <span className="manual-accounting-year flex flex-row">
                                                        <span className="flex shrink-0 mr-2">{t`Select manually`}</span>
                                                        <Field.DatePicker name="from" fieldClassName="mr-1" />
                                                        -
                                                        <Field.DatePicker name="to" fieldClassName="ml-1" />
                                                    </span>
                                                ),
                                                value: 2,
                                            },
                                        ]}
                                    />

                                    <SubmitErrors errors={submitErrors} />
                                </div>

                                <div className="onboarding__navigation-buttons">
                                    <Button appearance="primary" type="submit">
                                        <Trans>Next</Trans>
                                    </Button>
                                    <Button
                                        appearance="discrete"
                                        onClick={e => {
                                            e.preventDefault();
                                            onBack();
                                        }}>
                                        <Trans>Back</Trans>
                                    </Button>
                                </div>
                            </>
                        )}
                    </Form>
                )}
            </div>
        </div>
    );
}
